import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { trpc, trpcClient } from '@/lib/trpc';
import { useAuth } from '@/lib/auth';
import { useOnlineStatus } from './useOnlineStatus';
import {
  listOutbox,
  removeFromOutbox,
  bumpAttempts,
  markDelivered,
  subscribeOutbox,
  type OutboxItem,
} from './outbox';
import {
  drainOutbox,
  belongsTo,
  MAX_SEND_ATTEMPTS,
  type DrainResult,
  type QueuedShopChatPayload,
} from './drainOutbox';

/**
 * Owns the offline queue for the whole app.
 *
 * This used to be a `useEffect` inside the shop-chat channel view, so the queue
 * only emptied while a tech was looking at that one screen. Queue a message in
 * the back bay, walk to the front counter, tap Diagnose — nothing sent, and the
 * banner said "Catching up" for the rest of the shift.
 *
 * The second enqueue path is worse: it fires when a send throws a network error
 * while the browser still believes it is online, so there is no offline→online
 * flip to trigger anything. Hence the retry loop, which runs only while
 * something is actually waiting and backs off rather than hammering.
 */
export interface OutboxState {
  online: boolean;
  /** This user's messages only. The tablet is shared; the queue is not. */
  queued: OutboxItem[];
  /** Waiting to send, and still worth offering. */
  pendingCount: number;
  /** The server refused these too many times. We've stopped offering them. */
  stuck: OutboxItem[];
  draining: boolean;
  /** Set when we cannot read the queue at all — an empty queue and a blind one differ. */
  storageError: boolean;
  discardStuck: () => Promise<void>;
}

const RETRY_BASE_MS = 30_000;
const RETRY_MAX_MS = 10 * 60_000;
/** A send that neither resolves nor rejects would hold the drain lock forever. */
const SEND_TIMEOUT_MS = 30_000;

function sendSignal(): AbortSignal | undefined {
  if (typeof AbortSignal === 'undefined' || typeof AbortSignal.timeout !== 'function') {
    return undefined;
  }
  return AbortSignal.timeout(SEND_TIMEOUT_MS);
}

/**
 * One drain across the whole device, not one per tab.
 *
 * A React ref only guards this tab. A shop tablet commonly has the installed
 * app and a browser tab on the same origin, sharing one IndexedDB — both would
 * see the same row, and `messages.send` has no idempotency key, so the message
 * posts twice.
 */
async function withDrainLock<T>(fn: () => Promise<T>): Promise<T | null> {
  const locks = (navigator as Navigator & { locks?: LockManager }).locks;
  if (!locks?.request) return fn();
  return locks.request('fleetwise-outbox-drain', { ifAvailable: true }, async (lock) =>
    lock ? fn() : null,
  );
}

export function useOutbox(): OutboxState {
  const online = useOnlineStatus();
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const utils = trpc.useUtils();
  const [queued, setQueued] = useState<OutboxItem[]>([]);
  const [draining, setDraining] = useState(false);
  const [storageError, setStorageError] = useState(false);
  const inFlight = useRef(false);

  const refresh = useCallback(async () => {
    try {
      const items = await listOutbox();
      setStorageError(false);
      setQueued(items);
      return items;
    } catch {
      // An unreadable queue is not an empty queue. Saying "nothing waiting"
      // here would be the same lie this whole change exists to remove.
      setStorageError(true);
      setQueued([]);
      return [] as OutboxItem[];
    }
  }, []);

  const run = useCallback(async (): Promise<DrainResult | null> => {
    if (!userId || inFlight.current) return null;
    inFlight.current = true;
    setDraining(true);
    try {
      return await withDrainLock(async () => {
        const result = await drainOutbox({
          userId,
          list: listOutbox,
          send: (payload: QueuedShopChatPayload) =>
            trpcClient.messages.send.mutate(payload, { signal: sendSignal() }),
          remove: removeFromOutbox,
          bump: bumpAttempts,
          markDelivered,
        });

        if (result.sent > 0) {
          toast.success(
            result.sent === 1 ? 'Held message sent.' : `${result.sent} held messages sent.`,
          );
          // Only the channels that actually received something — the old code
          // refetched whichever channel happened to be open instead.
          for (const channelId of result.channelIds) {
            utils.messages.listForChannel.invalidate({ channelId }).catch(() => undefined);
          }
        }
        // A message we simply couldn't get out says nothing: the bar already
        // shows it waiting, and a toast every retry through a long outage is
        // its own kind of noise. Only the shop refusing it is news.
        if (result.rejected > 0) {
          toast.error(
            result.rejected === 1
              ? "The shop wouldn't take a held message — it's still saved."
              : `The shop wouldn't take ${result.rejected} held messages — they're still saved.`,
          );
        }
        return result;
      });
    } finally {
      inFlight.current = false;
      setDraining(false);
      await refresh();
    }
  }, [userId, refresh, utils]);

  // Read the queue on mount and whenever anything writes to it, and send as
  // soon as we believe there's a network. No polling — the store tells us.
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;

    const sync = async () => {
      const items = await refresh();
      if (cancelled || !navigator.onLine) return;
      if (items.some((i) => belongsTo(i, userId) && i.attempts < MAX_SEND_ATTEMPTS)) {
        await run();
      }
    };

    void sync();
    const unsubscribe = subscribeOutbox(() => void refresh());
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [userId, online, refresh, run]);

  const mine = userId ? queued.filter((i) => belongsTo(i, userId)) : [];
  const stuck = mine.filter((i) => !i.deliveredAt && i.attempts >= MAX_SEND_ATTEMPTS);
  const pendingCount = mine.filter((i) => !i.deliveredAt && i.attempts < MAX_SEND_ATTEMPTS).length;

  // Retry loop, alive only while something is waiting. This is what catches the
  // enqueue-on-network-error case, where the browser never reports going
  // offline and so never reports coming back. It backs off so a long outage
  // doesn't mean a request every thirty seconds for an hour.
  const backoff = useRef(RETRY_BASE_MS);
  useEffect(() => {
    if (!userId || pendingCount === 0) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;

    const schedule = (ms: number) => {
      timer = setTimeout(() => {
        void (async () => {
          if (cancelled) return;
          if (navigator.onLine) {
            const result = await run();
            backoff.current =
              result && (result.sent > 0 || result.rejected > 0)
                ? RETRY_BASE_MS
                : Math.min(backoff.current * 2, RETRY_MAX_MS);
          }
          if (!cancelled) schedule(backoff.current);
        })();
      }, ms);
    };

    schedule(backoff.current);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [userId, pendingCount, run]);

  const discardStuck = useCallback(async () => {
    if (!userId) return;
    const items = await listOutbox().catch(() => [] as OutboxItem[]);
    const doomed = items.filter(
      (i) => belongsTo(i, userId) && !i.deliveredAt && i.attempts >= MAX_SEND_ATTEMPTS,
    );
    for (const item of doomed) {
      await removeFromOutbox(item.id).catch(() => undefined);
    }
    await refresh();
  }, [userId, refresh]);

  return { online, queued: mine, pendingCount, stuck, draining, storageError, discardStuck };
}
